import { useEffect, useMemo, useState } from 'react';
import { Form, Stack } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import { TABLE_CONTEXT } from 'src/services/posthogEvents';

import useLocalStore from 'src/store/useLocalStore';

import ColumnConfiguration from 'src/components/custom_components/ColumnConfiguration';
import CustomTable, {
  Column,
} from 'src/components/custom_components/CustomTable/CustomTable';
import OrderStatusBadge from 'src/components/custom_components/OrderStatusBadge';
import SitesDropdown from 'src/components/custom_components/SitesDropdown';
import OrderDetailsButton from 'src/components/OrderDetailsButton';
import OrderDetailsDrawer from 'src/components/OrderDetailsDrawer';
import SearchPatientInfoFormGroup from 'src/components/SearchPatientInfoFormGroup/SearchPatientInfoFormGroup';

import { DEFAULT_PAGE_SIZE } from 'src/models/DefaultPageSize.model';
import { DeviceDataView } from 'src/models/GalenData/DeviceDataView.model';
import { OrderData, OrderStatusEnum } from 'src/models/GalenData/Order.model';
import { I18NextNS } from 'src/models/Language.model';

import useColumnVisibility from 'src/hooks/useColumnVisibility';
import useGetExistingSiteData from 'src/hooks/useGetExistingSiteData';
import { useGetFilterOrder } from 'src/hooks/useGetFilterOrder';

const DISPLAYED_STATUSES = [
  OrderStatusEnum.PENDING,
  OrderStatusEnum.COMPLETE,
  OrderStatusEnum.CLOSED,
  OrderStatusEnum.CANCELLED,
];

const DEFAULT_COLUMN_VISIBILITY = { userEmail: false };

const TeleophthResults = () => {
  const { t } = useTranslation(I18NextNS.PAGES);
  const { t: tCommon } = useTranslation(I18NextNS.COMMON);

  const [isOrderDetailsDrawerOpen, setIsOrderDetailsDrawerOpen] =
    useState(false);
  const [selectedOrder, setSelectedOrder] =
    useState<DeviceDataView<OrderData>>();

  const { isLoadingSites, existingSites, getExistingSiteData } =
    useGetExistingSiteData();

  const {
    selectedSiteId,
    teleophthIntegratedResultsColsKeys,
    setTeleophthIntegratedResultsColsKeys,
  } = useLocalStore();

  const {
    currentPage,
    handleSetCurrentPage,
    isGettingOrders: isLoading,
    dataResource,
    userEmailsByOrderId,
    handleLoadOrders,
    searchValues,
    handleSetSearchValues,
  } = useGetFilterOrder();

  useEffect(() => {
    getExistingSiteData();
    handleLoadOrders(0, DEFAULT_PAGE_SIZE, selectedSiteId, DISPLAYED_STATUSES);
  }, [getExistingSiteData, handleLoadOrders, selectedSiteId]);

  const columns: Column<DeviceDataView<OrderData>>[] = useMemo(
    () => [
      {
        title: tCommon('patientFields.userEmail'),
        dataIndex: 'userEmail',
        render: (data: DeviceDataView<OrderData>) =>
          userEmailsByOrderId[data.data.OrderId.value ?? ''],
      },
      {
        title: tCommon('patientFields.dateTime'),
        dataIndex: 'dateTime',
        render: (data: DeviceDataView<OrderData>) => {
          const datetime = data.maxValueProvidedOn;
          if (!datetime) return null;
          const local = moment.utc(datetime).local();
          return (
            <>
              <div>
                <b>{local.format('YYYY-MM-DD')}</b>
              </div>
              <div>{local.format('HH:mm:ss')}</div>
            </>
          );
        },
      },
      {
        title: tCommon('patientFields.First Name'),
        dataIndex: 'firstName',
        render: (data: DeviceDataView<OrderData>) => data.owner?.firstName,
      },
      {
        title: tCommon('patientFields.Last Name'),
        dataIndex: 'lastName',
        render: (data: DeviceDataView<OrderData>) => data.owner?.lastName,
      },
      {
        title: tCommon('patientFields.PID'),
        dataIndex: 'patientId',
        render: (data: DeviceDataView<OrderData>) => data.owner?.patientId,
      },
      {
        title: tCommon('patientFields.DOB'),
        dataIndex: 'dob',
        render: (data: DeviceDataView<OrderData>) => data.owner?.dateOfBirth,
      },
      {
        title: tCommon('patientFields.siteName'),
        dataIndex: 'siteName',
        render: (data: DeviceDataView<OrderData>) => data.data?.SiteName?.value,
      },
      {
        title: tCommon('orderFields.orderStatus'),
        dataIndex: 'orderStatus',
        render: (data: DeviceDataView<OrderData>) =>
          OrderStatusBadge(data.data?.Status.value),
      },
      {
        title: tCommon('orderFields.actions'),
        dataIndex: 'actions',
        render: (data: DeviceDataView<OrderData>) => (
          <OrderDetailsButton
            onClick={() => {
              setSelectedOrder(data);
              setIsOrderDetailsDrawerOpen(true);
            }}
          />
        ),
      },
    ],
    [tCommon, userEmailsByOrderId],
  );

  const { visibleColumns, handleToggleColumn, displayedColumns } =
    useColumnVisibility(
      columns,
      teleophthIntegratedResultsColsKeys,
      setTeleophthIntegratedResultsColsKeys,
      {
        defaultVisibility: DEFAULT_COLUMN_VISIBILITY,
        tableContext: TABLE_CONTEXT.RESULTS,
      },
    );

  return (
    <Stack className="h-100" gap={3}>
      <Stack
        direction="horizontal"
        className="justify-content-between align-items-center"
      >
        <h3 className="mb-0">{t('results.title')}</h3>
      </Stack>
      <Stack direction="horizontal" gap={3} className="align-items-end">
        <div>
          <Form.Label>{tCommon('sites.title')}</Form.Label>
          <SitesDropdown
            toggleClassName="py-2"
            isLoadingSites={isLoadingSites}
            existingSites={existingSites}
            handleSiteChange={(siteId) => {
              handleLoadOrders(
                0,
                DEFAULT_PAGE_SIZE,
                siteId,
                DISPLAYED_STATUSES,
                searchValues,
              );
            }}
          />
        </div>
        <SearchPatientInfoFormGroup
          onSearchValuesChange={(values) => {
            handleSetSearchValues(values);
            handleLoadOrders(
              0,
              DEFAULT_PAGE_SIZE,
              selectedSiteId,
              DISPLAYED_STATUSES,
              values,
            );
          }}
          onFormReset={() => {
            handleSetSearchValues(undefined);
            handleLoadOrders(
              0,
              DEFAULT_PAGE_SIZE,
              selectedSiteId,
              DISPLAYED_STATUSES,
            );
          }}
        />
        <ColumnConfiguration
          columns={columns}
          visibleColumns={visibleColumns}
          onToggleColumn={handleToggleColumn}
        />
      </Stack>
      <div className="flex-grow-1" style={{ minHeight: 0 }}>
        <CustomTable
          isLoading={isLoading}
          dataSource={dataResource.content}
          rowKey="deviceDataId"
          columns={displayedColumns}
          pagination={{
            defaultPageSize: DEFAULT_PAGE_SIZE,
            pageSizeOptions: [DEFAULT_PAGE_SIZE],
            totalPages: dataResource.totalPages,
            currentPage,
            handleSetCurrentPage,
            onChangePage: (page, pageSize) => {
              handleLoadOrders(
                page - 1,
                pageSize,
                selectedSiteId,
                DISPLAYED_STATUSES,
                searchValues,
              );
            },
          }}
          empty={{ buttons: [] }}
        />
      </div>
      <OrderDetailsDrawer
        show={isOrderDetailsDrawerOpen}
        onHide={() => setIsOrderDetailsDrawerOpen(false)}
        order={selectedOrder}
        showOrderStatus={true}
      />
    </Stack>
  );
};

export default TeleophthResults;
